import { Chip } from '@/components/ui'
import { useIntakeDetail } from '../api/queries'
import { lineMatchChip } from '../utils/line-match-chip'

export interface IntakeLinesPreviewProps {
  intakeId: string
}

function formatMoney(value: string | null): string {
  if (value == null) return '—'
  return `$${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

/**
 * Read-only line-item preview rendered inline below an expanded Sales
 * Inbox table row. Fetches lazily via `useIntakeDetail` — only mounted
 * while its row is expanded — so collapsed rows never pay for the
 * per-intake detail call. Editing stays on the intake work-view page;
 * this is a quick scan surface only.
 */
export function IntakeLinesPreview({ intakeId }: IntakeLinesPreviewProps) {
  const { data, isLoading, isError } = useIntakeDetail(intakeId)

  if (isLoading) {
    return (
      <div className="px-[var(--cell-padding-x)] py-3 text-sm text-tertiary">
        Loading lines…
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="px-[var(--cell-padding-x)] py-3 text-sm text-danger">
        Couldn&apos;t load lines for this order.
      </div>
    )
  }

  return (
    <div className="overflow-x-auto px-[var(--cell-padding-x)] py-2">
      <table className="min-w-full text-sm">
        <thead className="text-role-table-header">
          <tr>
            <th className="px-2 py-1.5 text-left">#</th>
            <th className="px-2 py-1.5 text-left">Part #</th>
            <th className="px-2 py-1.5 text-left">Description</th>
            <th className="px-2 py-1.5 text-left">SPI Product</th>
            <th className="px-2 py-1.5 text-right">Qty</th>
            <th className="px-2 py-1.5 text-right">Unit Price</th>
            <th className="px-2 py-1.5 text-left">Match</th>
          </tr>
        </thead>
        <tbody>
          {data.lines.map((line) => {
            const match = lineMatchChip(line)
            const qty = line.final_qty ?? line.parsed_qty
            const price = line.final_unit_price ?? line.parsed_unit_price
            return (
              <tr key={line.id} className="border-t border-border-subtle text-role-table-cell">
                <td className="px-2 py-1.5 text-tertiary">{line.line_no}</td>
                <td className="px-2 py-1.5 text-secondary">{line.parsed_part_number ?? '—'}</td>
                <td className="px-2 py-1.5 text-secondary">
                  <span
                    className="block max-w-[28ch] truncate"
                    title={line.parsed_description ?? ''}
                  >
                    {line.parsed_description ?? '—'}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-secondary">
                  {line.matched_product_name ?? <em className="text-tertiary">unmatched</em>}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-secondary">
                  {qty ?? '—'}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums text-secondary">
                  {formatMoney(price)}
                </td>
                <td className="px-2 py-1.5">
                  <Chip size="xs" variant={match.variant} dot>
                    {match.label}
                  </Chip>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
